import React, {
    forwardRef,
    ForwardRefExoticComponent,
    RefAttributes,
} from 'react';
import { getClonedChild, Group } from '@landr/maestro';
import ExclusivePackBadge from 'components/ExclusiveCardBadge/ExclusiveCardBadge';
import ConditionalLink from 'components/ConditionalLink/ConditionalLink';
import * as Styled from './Card.styled';
import Actions from './Actions/Actions';
import BackgroundImage from './BackgroundImage/BackgroundImage';
import Title from './Title/Title';
import Byline from './Byline/Byline';
import { CardProps } from './Card.types';

interface CardSubComponents {
    Actions: typeof Actions;
    BackgroundImage: typeof BackgroundImage;
    Title: typeof Title;
    Byline: typeof Byline;
}

type CardComponent = ForwardRefExoticComponent<
    CardProps & RefAttributes<HTMLElement>
> &
    CardSubComponents;

const Card = forwardRef<
    HTMLDivElement,
    CardProps & React.RefAttributes<HTMLDivElement>
>(
    (
        {
            children,
            linkTo,
            bylineLinkTo,
            isExclusive,
            backgroundColor = '#142739',
            isLoading = false,
            onLinkClick,
        },
        ref,
    ) => {
        const childrenArray = React.Children.toArray(children);

        const backgroundImage = getClonedChild(
            childrenArray,
            'Card.BackgroundImage',
            {
                key: 'backgroundImage',
                isLoading: isLoading,
            },
        );
        const actions = getClonedChild(childrenArray, 'Card.Actions', {
            key: 'actions',
            isLoading: isLoading,
        });
        const title = getClonedChild(childrenArray, 'Card.Title', {
            key: 'title',
            isLoading: isLoading,
        });
        const byline = getClonedChild(childrenArray, 'Card.Byline', {
            key: 'byline',
            isLoading: isLoading,
        });

        return (
            <Styled.CardWrapper ref={ref}>
                <Group
                    direction="vertical"
                    gutter="xxs"
                    aria-live="polite"
                    aria-busy={isLoading}
                >
                    <Group.Item>
                        <ConditionalLink to={linkTo} onLinkClick={onLinkClick}>
                            <Group direction="vertical" gutter="sm">
                                <Group.Item>
                                    <Styled.CardTop
                                        backgroundColor={backgroundColor}
                                    >
                                        {backgroundImage}
                                        {isExclusive && (
                                            <ExclusivePackBadge
                                                isAbsolute={true}
                                            />
                                        )}
                                        {actions}
                                    </Styled.CardTop>
                                </Group.Item>
                                <Group.Item>{title}</Group.Item>
                            </Group>
                        </ConditionalLink>
                    </Group.Item>
                    <Group.Item>
                        <ConditionalLink to={bylineLinkTo}>
                            {byline}
                        </ConditionalLink>
                    </Group.Item>
                </Group>
            </Styled.CardWrapper>
        );
    },
) as CardComponent;

Card.BackgroundImage = BackgroundImage;
Card.Actions = Actions;
Card.Title = Title;
Card.Byline = Byline;

export default Card;
